import { t } from 'ttag'
import YumButton from '../../../../components/YumButton/YumButton'
import React, { useState, useEffect, useRef } from 'react'
import { CompanyId, CreateTeam$Request, DivisionCategoryId } from '../../../../services/api.types'
import { darkBlue, primaryBlue } from '../../../../styles/material-ui-theme-v2'
import { ReactComponent as AddRoundedBigIcon } from '../../../../assets/v3/icons/add-rounded-big.svg'
import YumDialog from '../../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import YumInput from '../../../../components/YumInput/YumInput'
import { ReactComponent as GroupActiveIcon } from '../../../../assets/v3/icons/group-active.svg'
import NotificationsWrapper from '../../../../components/Notification/NotificationsWrapper'
import { clearNotification } from '../../../../common/notifications/NotificationActions'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'
import { store } from '../../../../index'
import { isRtl } from '../../../../i18nInit'

export interface ICreateTeam {
  variant: 'button' | 'link'
  text: string
  companyId: CompanyId
  isUserCreated: boolean
  isAdminView?: boolean
  lock?: boolean
  isDuringRegistration?: boolean
  style?: object
  createdDivisionId?: string | null
  createTeamLoading?: CompanyId | null
  selectedGroupId?: DivisionCategoryId | null
}

export interface ICreateTeamDispatchProps {
  createTeam(payload: CreateTeam$Request & { silentSuccess: boolean }): void
  resetCreateTeamLoading(): void
  clearNotification(): void
}

export default function CreateTeam(props: ICreateTeam & ICreateTeamDispatchProps) {
  const {
    variant,
    text: textRaw,
    companyId,
    isUserCreated,
    lock,
    isDuringRegistration,
    style,
    createTeam,
    createdDivisionId,
    createTeamLoading,
    resetCreateTeamLoading,
    selectedGroupId,
    clearNotification,
    isAdminView,
  } = props

  // Check if this specific company is in loading state
  const isThisCompanyLoading = createTeamLoading === companyId
  const text = isThisCompanyLoading ? t`Creating...` : textRaw

  const [dialogOpen, setDialogOpen] = useState(false)
  const [teamName, setTeamName] = useState('')
  const [nameError, setNameError] = useState('')

  // Use refs to track previous values for comparison
  const prevCreatedDivisionIdRef = useRef(createdDivisionId)
  const prevCreateTeamLoadingRef = useRef(createTeamLoading)

  // Monitor both createdDivisionId and createTeamLoading to detect successful team creation
  useEffect(() => {
    // Check if we've gone from loading this company to not loading (completed) AND we have a new division ID
    const wasThisCompanyLoading = prevCreateTeamLoadingRef.current === companyId
    const isNotLoadingNow = createTeamLoading !== companyId
    const hasCompletedLoading = wasThisCompanyLoading && isNotLoadingNow
    const hasDivisionId = createdDivisionId !== null && createdDivisionId !== undefined

    if (hasCompletedLoading && hasDivisionId) {
      console.log('Team creation completed successfully', createdDivisionId)
      setDialogOpen(false)
    }

    // Update refs with current values
    prevCreatedDivisionIdRef.current = createdDivisionId
    prevCreateTeamLoadingRef.current = createTeamLoading
  }, [createdDivisionId, createTeamLoading, companyId])

  // Clean up createTeamLoading flag when component unmounts
  /*useEffect(() => {
    return () => {
      // Reset the flag when component unmounts to prevent it from persisting in global state
      resetCreateTeamLoading()
    }
  }, [resetCreateTeamLoading])*/

  // Handle failed team creation
  /*useEffect(() => {
    // Check if createTeamLoading has changed to true while loading
    if (createTeamLoading && createTeamLoading && createTeamLoading !== prevcreateTeamLoadingRef.current) {
      console.log('createTeamLoading changed', createTeamLoading, prevcreateTeamLoadingRef.current)
      setcreateTeamLoading(false)
      // We can either close the dialog or keep it open to let the user try again
      // For this implementation, we'll close it on failure as well
      setDialogOpen(false)
    }

    // Update ref with current value
    prevcreateTeamLoadingRef.current = createTeamLoading
  }, [createTeamLoading, createTeamLoading])*/

  const handleDialogOpen = () => {
    // TODO: This prevents opening the modal in companies table on settings page
    // Clear notifications before opening the modal
    if (isAdminView) {
      clearNotification()
    }

    setDialogOpen(true)
    setTeamName('')
    setNameError('')
  }

  const handleDialogClose = () => {
    if (!isThisCompanyLoading) {
      setDialogOpen(false)
    }
  }

  const handleTeamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTeamName(e.target.value)
    if (nameError) setNameError('')
  }

  const handleCreateTeam = () => {
    if (isThisCompanyLoading) return
    if (!teamName.trim()) {
      setNameError(selectedGroupId ? t`Please enter a group name.` : t`Please enter a team name.`)
      return
    }

    createTeam({
      companyId,
      name: teamName.trim(),
      isUserCreated,
      lock: !!lock,
      silentSuccess: !!isDuringRegistration,
      groupId: selectedGroupId || undefined,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && teamName.trim() && !isThisCompanyLoading) {
      handleCreateTeam()
    }
  }

  /*useEffect(() => {
    console.log('createTeam mount')
  }, [])

  useEffect(() => {
    console.log('createTeamLoading', createTeamLoading)
  }, [createTeamLoading])*/

  return (
    <>
      {isDuringRegistration ? (
        <YumButton
          onClick={handleDialogOpen}
          variant="text"
          size="small"
          style={{
            display: 'block',
            color: darkBlue,
            padding: '10px 12px',
            margin: '6px 0 0',
            fontWeight: 400,
            height: 'auto',
            fontSize: '1rem',
            lineHeight: '24px',
            ...style,
          }}
          disabled={isThisCompanyLoading}
        >
          <AddRoundedBigIcon style={{ marginRight: isRtl ? '0px' : '8px', marginLeft: !isRtl ? '0px' : '8px' }} />
          {text}
        </YumButton>
      ) : variant === 'button' ? (
        <YumButton
          color="secondary"
          onClick={handleDialogOpen}
          variant="contained"
          size="small"
          disabled={isThisCompanyLoading}
        >
          {text}
        </YumButton>
      ) : (
        <a
          href={undefined}
          onClick={handleDialogOpen}
          style={{
            color: primaryBlue,
            textDecoration: 'none',
            marginTop: '5px',
            display: 'inline-block',
            cursor: isThisCompanyLoading ? 'default' : 'pointer',
          }}
        >
          {text}
        </a>
      )}
      <YumDialog open={dialogOpen} aria-labelledby="create-team-dialog-title">
        {/* <form autoComplete="off"> */}
        <YumDialogTitle id="create-team-dialog-title" handleClose={handleDialogClose}>
          {selectedGroupId ? t`Create new group` : t`Create new team`}
        </YumDialogTitle>
        <YumDialogContent>
          <NotificationsWrapper />
          <YumInput
            startIcon={GroupActiveIcon}
            autoFocus
            id="teamName"
            label={selectedGroupId ? t`Group name` : t`Team name`}
            fullWidth
            value={teamName}
            onChange={handleTeamNameChange}
            onKeyDown={handleKeyDown}
            style={{ marginBottom: 0 }}
            disabled={isThisCompanyLoading}
          />
          {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={handleDialogClose} disabled={isThisCompanyLoading} />
          <YumSubmitButton
            text={t`Create`}
            onClick={handleCreateTeam}
            disabled={isThisCompanyLoading}
            submitInProgress={isThisCompanyLoading}
            immersiveLoader={true}
          />
        </YumDialogActions>
        {/* </form> */}
      </YumDialog>
    </>
  )
}
